import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { audioService } from "../services/audioService";
import Reveal from "../components/Reveal";
import ErrorState from "../components/ErrorState";
import useSEO from "../hooks/useSEO";

const SPEEDS = [1, 1.25, 1.5, 2];

const formatTime = (time) => {
  if (isNaN(time) || time == null) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Small animated bars shown while audio is playing
const PlayingIndicator = () => (
  <span className="inline-flex items-end gap-[2px] h-3.5" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-[3px] rounded-full bg-blue-600 animate-equalize origin-bottom"
        style={{
          height: "100%",
          animationDelay: `${i * 0.2}s`,
          animationDuration: `${0.8 + i * 0.2}s`,
        }}
      />
    ))}
  </span>
);

const AudioDetail = () => {
  const { id } = useParams();
  const audioRef = useRef(null);
  const [audio, setAudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAudio = async () => {
      try {
        setLoading(true);
        const data = await audioService.getAudioById(id);
        if (!data) {
          setError("Audio not found");
        } else {
          setAudio(data);
        }
      } catch (err) {
        setError("Failed to load audio details");
      } finally {
        setLoading(false);
      }
    };

    fetchAudio();
  }, [id]);

  // Auto-play when the episode loads (browsers may block this)
  useEffect(() => {
    if (audioRef.current && audio && audio.audioUrl) {
      const timer = setTimeout(async () => {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.log("Auto-play prevented by browser:", error);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [audio]);

  const handlePlayPause = () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
    } else {
      el.play();
    }
  };

  const handleSeek = (e) => {
    const el = audioRef.current;
    if (!el) return;
    const newTime = Number(e.target.value);
    el.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleSkip = (seconds) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const newTime = Math.min(Math.max(el.currentTime + seconds, 0), duration);
    el.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolume = (e) => {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) audioRef.current.volume = newVolume;
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const handleShare = async () => {
    const shareData = {
      title: audio.title,
      text: audio.description,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  useSEO({
    title: audio ? audio.title : "Episode",
    description: audio?.description
      ? audio.description.slice(0, 158)
      : "Listen to this AWS Daily Updates episode — AWS announcements as a short AI-generated audio briefing.",
    path: `/audio/${id}`,
    type: "article",
    noindex: !audio,
    jsonLd: audio
      ? {
          "@context": "https://schema.org",
          "@type": "PodcastEpisode",
          name: audio.title,
          url: `https://deeppatel0311.github.io/aws-podcast/audio/${id}`,
          description: audio.description,
          datePublished: audio.publishDate,
          associatedMedia: {
            "@type": "MediaObject",
            contentUrl: audio.audioUrl,
            encodingFormat: "audio/mpeg",
          },
          partOfSeries: {
            "@type": "PodcastSeries",
            name: "AWS Daily Updates",
            url: "https://deeppatel0311.github.io/aws-podcast/",
          },
        }
      : null,
  });

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="skeleton h-5 w-32 mb-8" />
        <div className="skeleton h-10 w-3/4 mb-4" />
        <div className="skeleton h-4 w-full mb-2" />
        <div className="skeleton h-4 w-2/3 mb-8" />
        <div className="skeleton h-44 w-full rounded-2xl mb-8" />
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-24 w-full rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !audio) {
    const notFound = !audio && (!error || error === "Audio not found");
    return (
      <ErrorState
        eyebrow={notFound ? "Episode not found" : "Something went wrong"}
        title={notFound ? "This episode is off the air" : "We couldn't load this episode"}
        message={
          notFound
            ? "The episode you're looking for doesn't exist or may have been removed. All available episodes are on the home page."
            : "There was a problem loading this episode. Please check your connection and try again."
        }
        onRetry={notFound ? undefined : () => window.location.reload()}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-blue-600 mb-8 transition-colors duration-200 group animate-fade-in"
      >
        <svg
          className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-200"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        All episodes
      </Link>

      {/* Episode header */}
      <div className="mb-8 animate-fade-up">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-block px-3 py-1 rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold">
            {audio.category || "AWS Updates"}
          </span>
          <span className="text-xs text-slate-400 font-medium">{audio.publishDate}</span>
          {audio.itemCount && (
            <span className="text-xs text-slate-400 font-medium">
              · {audio.itemCount} announcements
            </span>
          )}
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink-900 leading-tight mb-4">
          {audio.title}
        </h1>

        {audio.description && (
          <p className="text-slate-500 text-base sm:text-lg leading-relaxed">
            {audio.description}
          </p>
        )}
      </div>

      {/* Player */}
      <div className="card p-5 sm:p-6 mb-10 animate-fade-up" style={{ animationDelay: "100ms" }}>
        <div className="flex items-center gap-4 sm:gap-5">
          {/* Play / pause */}
          <div className="relative shrink-0">
            {isPlaying && (
              <span className="absolute inset-0 rounded-full bg-blue-500/50 animate-pulse-ring" />
            )}
            <button
              onClick={handlePlayPause}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white flex items-center justify-center shadow-sm transition-all duration-200"
            >
              {isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* Seek + status */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {isPlaying && <PlayingIndicator />}
              <span className="text-xs font-medium text-slate-500 truncate">
                {isPlaying ? "Now playing" : "Ready to play"}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              aria-label="Seek"
              className="w-full block"
              style={{
                background: `linear-gradient(to right, #2563eb ${progress}%, #e2e8f0 ${progress}%)`,
                height: "4px",
                borderRadius: "4px",
              }}
            />
            <div className="flex justify-between mt-1.5 text-[11px] font-medium text-slate-400 tabular-nums">
              <span className="text-slate-600">{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>

        {/* Secondary controls */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSkip(-15)}
              aria-label="Back 15 seconds"
              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 text-xs font-semibold transition-colors duration-200 flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
              </svg>
              15s
            </button>
            <button
              onClick={() => handleSkip(15)}
              aria-label="Forward 15 seconds"
              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 text-xs font-semibold transition-colors duration-200 flex items-center gap-1.5"
            >
              15s
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
              </svg>
            </button>
            <button
              onClick={cycleSpeed}
              aria-label="Playback speed"
              className="h-9 w-14 rounded-lg border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 text-xs font-semibold transition-colors duration-200"
            >
              {speed}×
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-2.5">
            <svg className="w-4 h-4 text-slate-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
            </svg>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={handleVolume}
              aria-label="Volume"
              className="w-24 block"
              style={{
                background: `linear-gradient(to right, #2563eb ${volume * 100}%, #e2e8f0 ${volume * 100}%)`,
                height: "4px",
                borderRadius: "4px",
              }}
            />
          </div>
        </div>

        <audio
          ref={audioRef}
          src={audio.audioUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
          className="hidden"
        />
      </div>

      {/* News items */}
      {audio.items && audio.items.length > 0 && (
        <div className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
            Covered in this episode
          </h2>
          <div className="space-y-3">
            {audio.items.map((item, index) => (
              <Reveal key={item.id || index} delay={Math.min(index, 6) * 50}>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group card block p-5 hover:shadow-md hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-200">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-ink-900 leading-snug mb-1.5 group-hover:text-blue-700 transition-colors duration-200">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-2">
                      {item.description}
                    </p>
                    <span className="text-xs text-slate-400">
                      {new Date(item.pubDate).toLocaleDateString()}
                    </span>
                  </div>
                  <svg
                    className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0 mt-1 transition-colors duration-200"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </a>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {/* Share */}
      <div className="flex justify-center">
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2.5 px-6 py-3 rounded-lg border border-slate-200 bg-white text-ink-900 text-sm font-semibold hover:border-blue-300 hover:text-blue-600 transition-colors duration-200"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Link copied!
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              Share episode
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AudioDetail;
